import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import MediaInput from "@/components/MediaInput";
import MediaViewer from "@/components/MediaViewer";
import { Plus, BookHeart, Pencil, Trash2, Sparkles, Loader2 } from "lucide-react";

const EMPTY = {
  title: "",
  body: "",
  story_type: "text",
  media_type: "text",
  media_data: "",
  media_filename: "",
  media_size: 0,
  transcript: "",
};

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = meta.match(/data:([^;]+)/)?.[1] || "audio/webm";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export default function Historia() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [transcribing, setTranscribing] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/stories");
    setItems(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.story_id);
    setForm({
      title: s.title,
      body: s.body || "",
      story_type: s.story_type,
      media_type: s.media_type || "text",
      media_data: s.media_data || "",
      media_filename: s.media_filename || "",
      media_size: s.media_size || 0,
      transcript: s.transcript || "",
    });
    setViewing(null);
    setOpen(true);
  };

  const transcribe = async () => {
    if (!form.media_data || form.media_type !== "audio") {
      toast.error(t("historia_no_audio_first"));
      return;
    }
    setTranscribing(true);
    try {
      const blob = dataUrlToBlob(form.media_data);
      const fd = new FormData();
      fd.append("file", blob, form.media_filename || "audio.webm");
      const r = await fetch(`${API}/transcribe`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!r.ok) throw new Error("fail");
      const data = await r.json();
      setForm((f) => ({ ...f, transcript: data.text, body: f.body || data.text }));
      toast.success(t("historia_transcribe_done"));
    } catch {
      toast.error(t("historia_transcribe_failed"));
    } finally {
      setTranscribing(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        story_type: form.media_type === "audio" ? "voice" : "text",
      };
      if (editingId) {
        const r = await api.put(`/stories/${editingId}`, payload);
        setItems((arr) => arr.map((x) => (x.story_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/stories", payload);
        setItems((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
    } catch {
      toast.error(t("settings_generic_error"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/stories/${id}`);
    toast.success(t("deleted"));
    setItems((arr) => arr.filter((x) => x.story_id !== id));
    if (viewing?.story_id === id) setViewing(null);
  };

  return (
    <div data-testid="historia-page">
      <PageHeader
        title={t("historia_title")}
        subtitle={t("historia_subtitle")}
        action={
          <button className="btn-gold" onClick={openCreate} data-testid="story-add-btn">
            <Plus className="w-4 h-4" /> {t("historia_add")}
          </button>
        }
      />

      {items.length === 0 ? (
        <EmptyState>—</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((s) => (
            <button
              key={s.story_id}
              onClick={() => setViewing(s)}
              className="glass-card rounded-md p-5 text-left hover:border-gold transition"
              data-testid={`story-${s.story_id}`}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle">
                  <BookHeart className="w-5 h-5 text-gold" strokeWidth={1.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-gold-light truncate">{s.title}</div>
                  <div className="text-sm text-muted truncate">
                    {s.story_type === "voice" ? "🎙 Voz" : s.body?.slice(0, 80) || "—"}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.title || ""}
        testid="story-view-modal"
      >
        {viewing && (
          <div className="space-y-4">
            {viewing.media_data && <MediaViewer media={viewing} />}
            {viewing.transcript && (
              <div className="glass-card rounded-md p-4">
                <div className="font-label text-gold mb-2 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Transcripción IA
                </div>
                <div className="text-gold-light whitespace-pre-wrap text-sm">
                  {viewing.transcript}
                </div>
              </div>
            )}
            {viewing.body && (
              <div className="text-gold-light whitespace-pre-wrap font-body leading-relaxed">
                {viewing.body}
              </div>
            )}
            <div className="text-xs text-faded">
              {new Date(viewing.created_at).toLocaleString()}
            </div>
            <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
              <button className="btn-ghost" onClick={() => remove(viewing.story_id)}>
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
              <button className="btn-gold" onClick={() => openEdit(viewing)}>
                <Pencil className="w-4 h-4" /> {t("edit")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("historia_add")}
        testid="story-modal"
      >
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="font-label text-faded block mb-2">{t("title")}</label>
            <input className="input-field" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="story-title" />
          </div>

          <MediaInput
            textLabel={t("content")}
            textValue={form.body}
            onTextChange={(v) => setForm({ ...form, body: v })}
            media={form}
            onMediaChange={(m) => setForm({ ...form, ...m })}
          />

          {form.media_type === "audio" && form.media_data && (
            <button
              type="button"
              onClick={transcribe}
              disabled={transcribing}
              className="btn-ghost w-full justify-center"
              data-testid="story-transcribe"
            >
              {transcribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {transcribing ? "Transcribiendo…" : "Transcribir con IA"}
            </button>
          )}

          {form.transcript && (
            <div className="glass-card rounded-md p-4">
              <div className="font-label text-gold mb-2 flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Transcripción
              </div>
              <div className="text-muted text-sm whitespace-pre-wrap">{form.transcript}</div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button type="submit" className="btn-gold" data-testid="story-save">{t("save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
